import { getAppHydration } from '@kurtosys/app-loader/dist/hydration.js';
import { getAppKey } from '@kurtosys/app-loader/dist/utils.js';
import { EventManager, models as eventManagerModels, SourceEventManager } from '@kurtosys/event-manager';
import type { GroupedEventManager } from '@kurtosys/grouped-event-manager';
import { components, models } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { IApplicationClientConfiguration } from '@kurtosys/types/api/applicationManager/IApplicationClientConfiguration.js';
import { IGetThemeResponseBody } from '@kurtosys/types/api/applicationManager/IGetThemeResponseBody.js';
import { TGetUserByTokenResponseBody } from '@kurtosys/types/api/auth/TGetUserByTokenResponseBody.js';
import { IGetApplicationAppConfigResponseBody } from '@kurtosys/types/api/config/IGetApplicationAppConfigResponseBody.js';
import { IFeature, IInputs, IListenForOptions } from '@kurtosys/types/app/index.js';
import { MockDataBase } from '@kurtosys/types/app/mockData/MockDataBase.js';
import { CacheOptions, IAppAuthentication, IApplicationAssetRegister } from '@kurtosys/types/appsInitialize/index.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { ISkeletonLoader, ISkeletonLoaders } from '@kurtosys/types/appsManager/index.js';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';
import { GlobalDisclaimerLevel } from '@kurtosys/types/disclaimers/GlobalDisclaimerLevel.js';
import { IGlobalSearchInputs } from '@kurtosys/types/eventBus/inputValues/IGlobalSearchInputs.js';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext.js';
import { IPixelDimensions } from '@kurtosys/types/layout/IPixelDimensions.js';
import { IGlobalFormatsByKey, IQueryContext } from '@kurtosys/types/query/index.js';
import stringify from 'json-stable-stringify';
import { action, computed, observable, runInAction } from 'mobx';

import { getStoreKey } from '../../appStartUtils/app.js';
import { getApplicationCode } from '../../appStartUtils/app.js';
import { getGlobalDisclaimerStore } from '../../appStartUtils/globalDisclaimers.js';
import { registerAppLoaded, registerAppStart } from '../../appStartUtils/initialize.js';
import { isDebug } from '../../appStartUtils/isDebug.js';
import { debug, error, ILogOptions, info, success, TLogType, warning } from '../../appStartUtils/log.js';
import { getPreviewContext } from '../../appStartUtils/previewContext.js';
import { IAppDevelopmentConfig } from '../../models/IAppDevelopmentConfig.js';
import { IComponentConfigurationsBase } from '../../models/IComponentConfigurationsBase.js';
import { IComponentStylesBase } from '../../models/IComponentStylesBase.js';
import { IConfigurationBase } from '../../models/IConfigurationBase.js';
import { ICoreConfigurations } from '../../models/ICoreConfigurations.js';
import { IDebugInfo } from '../../models/IDebugInfo.js';
import { IPaginationOptions } from '../../models/IPaginationOptions.js';
import { IStyles } from '../../models/IStyles.js';
import { TDataContextStatus } from '../../models/TDataContextStatus.js';
import { Breakpoints } from '../../shared/Breakpoints.js';
import { ResponsiveConfig } from '../../shared/ResponsiveConfig.js';
import { AccessibilityStore } from '../AccessibilityStore.js';
import { EventBusStore } from '../EventBusStore.js';
import { InputStore } from '../InputStore.js';
import { KurtosysApiStore } from '../KurtosysApiStore.js';
import { PaginationStore } from '../PaginationStore.js';
import { QueryStore } from '../QueryStore.js';
import { StoreContext } from '../StoreContext.js';
import { TranslationStore } from '../TranslationStore.js';

import { StoreBase } from './StoreBase.js';

export const DATA_CONTEXT_SEED_KEY = '__ksys-app-data-context-seed__';

export interface AppStoreBase<
	TConfiguration extends IConfigurationBase,
	TComponentStyles extends IComponentStylesBase,
> {
	/**
	 * This is an overridable AppStoreBase method.
	 * The method executes after we have initialized the data context, translations & theme.
	 *
	 * @returns void
	 */
	customInitializeAfter?(): Promise<void>;
	/**
	 * This is an overridable AppStoreBase method.
	 * The method executes before we fetch application configuration.
	 *
	 * @returns void
	 */
	customInitializeBeforeConfigFetch?(): Promise<void>;
	contextsDidUpdateBefore?(): Promise<void>;
	contextsDidUpdateAfter?(): Promise<void>;
	/**
	 * called by didRender
	 *
	 * @returns void
	 */
	customDidRender?(): Promise<void>;
	/**
	 * This fires at the start of handleSearchTermUpdate as part of the Global Search Change Event.
	 *
	 * @param {eventManagerModels.EventPublish<eventManagerModels.detail.EventGlobalSearchChangeDetail>} ev
	 * @returns void
	 */
	onBeforeSearchTermUpdate?(
		ev: eventManagerModels.EventPublish<eventManagerModels.detail.EventGlobalSearchChangeDetail>,
	): Promise<void>;
	/**
	 * This fires at the end of handleSearchTermUpdate as part of the Global Search Change Event.
	 *
	 * NOTE: `await this.inputStore.updateQueryInputs(values);` is still fired after this override method.
	 *
	 * @param {eventManagerModels.EventPublish<eventManagerModels.detail.EventGlobalSearchChangeDetail>} ev
	 * @returns void
	 */
	onAfterSearchTermUpdate?(
		ev: eventManagerModels.EventPublish<eventManagerModels.detail.EventGlobalSearchChangeDetail>,
	): Promise<void>;
	/**
	 * If the app is to communicate with a pagination app, we need to provide the main query and/or overriding execution options.
	 * e.g. table.dynamicRows within the Table App.
	 */
	paginationOptions?: IPaginationOptions;
}

export abstract class AppStoreBase<
	TConfiguration extends IConfigurationBase,
	TComponentStyles extends IComponentStylesBase,
> extends StoreBase<TConfiguration, TComponentStyles> {
	abstract get hasData(): boolean;

	htmlElement: HTMLElement;
	storeKey: string;
	appParamsHelper: common.helpers.AppParamsHelper<IInputs, IConfigurationBase, IStyles<TComponentStyles>>;
	analyticsHelper: common.helpers.AnalyticsHelper<IAppContext>;
	groupedEventManager: GroupedEventManager;
	breakpointsFeature: IFeature;
	devConfig: IAppDevelopmentConfig;
	mockData?: MockDataBase;

	@observable.ref rawConfiguration: Partial<IConfigurationBase> = {};
	@observable.ref preventAppLoadedEventFire = false;
	@observable.ref rawStyles: Partial<IStyles<TComponentStyles>> = {};
	@observable.ref rawThemeResponse: IGetThemeResponseBody | null = null;
	@observable.ref authentication: IAppAuthentication | undefined;
	@observable.ref applicationClientConfigurationIds: number[] | undefined;
	@observable.ref applicationClientConfigurations: IApplicationClientConfiguration[] = [];
	@observable themeKey: string | undefined;
	@observable.ref public user: TGetUserByTokenResponseBody | undefined;
	@observable searchTerm = '';
	@observable.ref
	private pagination: PaginationStore<TConfiguration, TComponentStyles>;
	// App loading states
	@observable isBootstrapped = false;
	@observable isRendered = false;
	@observable _dataContextStatus: TDataContextStatus = 'PENDING';
	dataContextTimestamp: number | undefined;
	@computed
	get dataContextStatus(): TDataContextStatus {
		return this._dataContextStatus;
	}

	set dataContextStatus(status: TDataContextStatus) {
		if (this._dataContextStatus !== status) {
			runInAction(() => {
				this._dataContextStatus = status;
			});
		}
	}

	/**
	 * This getter will only setup the PaginationStore if it is referenced,
	 * thus we should only make use of this getter when we need to update the pagination app,
	 * e.g. When the Table app requires pagination,
	 * 			which is only when we add limit and/or start props to the request driving the rows.
	 *
	 * @readonly
	 */
	get paginationStore() {
		if (!this.isPaginationEnabled()) {
			return;
		}
		if (!this.pagination) {
			this.pagination = new PaginationStore(this.storeContext, this.manifest);
		}
		return this.pagination;
	}

	@computed
	get inputStore() {
		return this.storeContext.get<InputStore<TConfiguration, TComponentStyles>>('inputStore');
	}

	@computed
	get queryStore() {
		return this.storeContext.get<QueryStore<TConfiguration, TComponentStyles>>('queryStore');
	}

	@computed
	get eventBusStore() {
		return this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
	}

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>,
		manifest: IManifest,
		feature: IFeature,
		devConfig: IAppDevelopmentConfig,
		mockData?: MockDataBase,
	) {
		super(storeContext, manifest);
		this.htmlElement = element;
		this.storeKey = getStoreKey(element, url, manifest);
		this.appParamsHelper = new common.helpers.AppParamsHelper(element, url);
		registerAppStart(manifest, this.appParamsHelper);
		this.themeKey = (this.appParamsHelper.rawAppParams && this.appParamsHelper.rawAppParams.themeKey) || 'default';
		this.analyticsHelper = new common.helpers.AnalyticsHelper<IAppContext>(this.getDefaultAnalyticsContext);
		this.breakpointsFeature = feature;
		this.devConfig = devConfig;
		this.mockData = mockData;
		EventManager.init();
	}

	/**
	 * Used by the AppStoreBase to determine if the built in PaginationStore should be instantiated and executed.
	 *
	 * The built in PaginationStore will setup event listeners and auto update inputs & context when the pagination app updates.
	 *
	 * The PaginationStore will also emit events to the pagination app, should the result counts update.
	 *
	 * @returns boolean
	 */
	private isPaginationEnabled(): boolean {
		if (!this.paginationOptions) {
			return false;
		}
		if (!this.queryStore) {
			return false;
		}
		if (
			!this.eventBusStore ||
			!this.eventBusStore.isListeningForEvent(eventManagerModels.EventTypes.PAGINATION_CHANGE)
		) {
			return false;
		}
		const pagingRequest = this.queryStore.getPagingRequest();
		if (!pagingRequest) {
			if (this.paginationOptions.localPagination && this.paginationOptions.localPagination.limit) {
				return this.paginationOptions.localPagination.limit !== undefined;
			}
			return false;
		}
		return pagingRequest.limit !== undefined;
	}

	getTranslateFunction = () => {
		const translationStore =
			this.storeContext.get<TranslationStore<TConfiguration, TComponentStyles>>('translationStore');
		return translationStore.translate;
	};

	getAccessibilityTextFunction = () => {
		const accessibilityStore =
			this.storeContext.get<AccessibilityStore<TConfiguration, TComponentStyles>>('accessibilityStore');
		return accessibilityStore.accessibilityText;
	};

	@computed
	get culture(): string {
		const translationStore =
			this.storeContext.get<TranslationStore<TConfiguration, TComponentStyles>>('translationStore');
		return (translationStore && translationStore.culture) || 'en-GB';
	}

	@computed
	get globalInputIdentifiers(): string[] {
		return (this.appComponentConfiguration && this.appComponentConfiguration.globalInputIdentifiers) || [];
	}

	@computed
	get applicationId(): string {
		return this.appComponentConfiguration && this.appComponentConfiguration.applicationId;
	}

	@computed
	get applicationTypeKey(): string {
		const applicationId = this.appComponentConfiguration.applicationId;
		const templateId = this.manifest.ksysAppTemplateId;
		return templateId + (applicationId ? `-${applicationId}` : '');
	}

	@computed
	get listenFor(): IListenForOptions[] {
		return (this.appComponentConfiguration && this.appComponentConfiguration.listenFor) || [];
	}

	/**
	 * Return a URL object based on the current url in the browser
	 *
	 * @readonly
	 */
	get currentUrl(): URL {
		return new URL(document.location.toString());
	}

	get currentUrlKey(): string {
		return btoa(`${this.currentUrl.host}${this.currentUrl.pathname}`);
	}

	get currentUrlAndSearchKey(): string {
		return btoa(`${this.currentUrl.host}${this.currentUrl.pathname}${this.currentUrl.search}`);
	}

	/**
	 * This is an overridable AppStoreBase method.
	 * The method executes before we initialize the data context, translations & theme.
	 */
	async customInitializeBefore() {
		const eventBusStore = this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
		eventBusStore.initialize();
	}

	async contextsDidUpdate() {
		if (this.contextsDidUpdateBefore) {
			await this.contextsDidUpdateBefore();
		}
		this.setAppCardProps();
		if (this.paginationStore && this.paginationStore.processApiResponse) {
			this.paginationStore.processApiResponse();
		}
		if (this.contextsDidUpdateAfter) {
			await this.contextsDidUpdateAfter();
		}
	}

	@action setIsBootstrapped() {
		this.isBootstrapped = true;
	}

	@computed
	get isDevelopment(): boolean {
		return this.manifest.isDevelopment;
	}

	@computed
	get applicationGuid(): string | undefined {
		return this.manifest.getApplicationGuid(this.appParamsHelper);
	}

	get isDebug(): boolean {
		return isDebug();
	}

	/**
	 * This method will search for global search values based on events received from a global search app, and trigger the updates
	 *
	 * @param identifier
	 * @param [storageType]
	 * @param [values]
	 * @returns
	 */
	public handleSearchTermUpdate = async (
		ev: eventManagerModels.EventPublish<eventManagerModels.detail.EventGlobalSearchChangeDetail>,
	) => {
		if (this.onBeforeSearchTermUpdate) {
			this.onBeforeSearchTermUpdate(ev);
		}
		const { searchTerm } = ev.detail;
		this.searchTerm = searchTerm;
		const values: IGlobalSearchInputs = {
			'ksys-search': searchTerm,
		};
		if (this.onAfterSearchTermUpdate) {
			this.onAfterSearchTermUpdate(ev);
		}
		// if we are using pagination, then the pagination will take control of updating the inputs along with its values.
		if (this.paginationStore && this.paginationStore.handleSearchTermUpdate) {
			await this.paginationStore.handleSearchTermUpdate(values);
		}
		// otherwise we update the search term input here
		else {
			await this.inputStore.updateQueryInputs(values);
		}
	};

	logDebug(action: string, detail?: any, additionalContext?: string) {
		this.log('debug', {
			detail,
			additionalContext,
			message: action,
		});
	}

	log = (type: TLogType, options: ILogOptions) => {
		let logger;
		const workingOptions = { ...options, configKey: this.configurationKey };
		switch (type) {
			case 'info':
				logger = info;
				break;
			case 'success':
				logger = success;
				break;
			case 'warning':
				logger = warning;
				break;
			case 'error':
				logger = error;
				break;
			case 'debug':
			default:
				logger = debug;
				break;
		}
		logger(workingOptions, this.manifest);
	};

	logDebugInfo = async (): Promise<void> => {
		const debugInfo = await this.getDebugInfo();
		const debugInfoString = JSON.stringify(debugInfo, null, '\t');
		console.info({ debugInfo });
		console.info('Debug Info copied to clipboard');
		common.commonUtils.copyToClipboard(debugInfoString);

		const filename = `debug_${getApplicationCode(this.manifest)}_${new Date().toISOString()}.txt`;
		const element = document.createElement('a');
		element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(debugInfoString));
		element.setAttribute('download', filename);

		element.style.display = 'none';
		document.body.appendChild(element);

		element.click();

		document.body.removeChild(element);
	};

	getDebugInfo = async (): Promise<IDebugInfo> => {
		const user = await this.getUser();
		return {
			user,
			manifest: this.manifest.serialize(),
			inputs: this.appParamsHelper.inputs,
			applicationCode: getApplicationCode(this.manifest),
			applicationClientConfigurationIds: this.applicationClientConfigurationIds,
			applicationClientConfigurations: this.applicationClientConfigurations,
			authentication: this.authentication,
			configuration: this.rawConfiguration,
			style: this.rawStyles,
			theme: this.rawThemeResponse,
			url: window.location.href,
			serviceUrl: this.serviceUrl,
		};
	};

	getDefaultAnalyticsContext = (): IAppContext => {
		const { rawAppParams = {} } = this.appParamsHelper;
		const { configurationKey = 'default' } = rawAppParams;
		const inputs: any = this.appParamsHelper.inputs;
		return {
			configurationKey,
			appTemplate: this.manifest.ksysAppTemplateId,
			appGuid: this.applicationGuid || 'unknown',
			embedInputs: inputs,
			timestamp: new Date().getTime(),
		};
	};

	@action
	async initialize() {
		if (this.customInitializeBeforeConfigFetch) {
			await this.customInitializeBeforeConfigFetch();
		}
		const response = await this.getAppConfig();
		if (response) {
			const kurtosysApiStore =
				this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');

			this.authentication = response.authentication;

			const token = (this.authentication && this.authentication.token) || '';
			kurtosysApiStore.token = token;
			this.applicationClientConfigurationIds = response.applicationClientConfigurationIds;

			runInAction(() => {
				this.rawStyles = response.applicationStyles as any as IStyles<TComponentStyles>;
			});

			const rawConfiguration = await this.buildConfiguration(
				response.applicationConfiguration as any as TConfiguration,
			);
			runInAction(() => {
				this.rawConfiguration = rawConfiguration as TConfiguration;
			});

			const stopInitializeConfiguration = await this.loadPreviewContext();
			if (!stopInitializeConfiguration) {
				await this.initializeConfiguration();
			}
		}
	}

	@action
	async reInitialize() {
		const stopInitializeConfiguration = await this.loadPreviewContext();
		if (!stopInitializeConfiguration) {
			await this.initializeConfiguration();
		}
	}

	async buildConfiguration(configuration: TConfiguration): Promise<TConfiguration> {
		let response: IConfigurationBase = {};
		const applicationClientConfigurationIds = this.applicationClientConfigurationIds || [];
		if (applicationClientConfigurationIds && applicationClientConfigurationIds.length > 0) {
			if (this.isDevelopment) {
				if (this.applicationClientConfigurations.length === 0) {
					this.applicationClientConfigurations = [
						...(this.devConfig.applicationClientConfigurations &&
							(await this.devConfig.applicationClientConfigurations())),
					];
				}
				const results = applicationClientConfigurationIds.map((id) => {
					const applicationClientConfiguration = this.applicationClientConfigurations.find(
						(a) => a.applicationClientConfigurationId === id,
					);
					return applicationClientConfiguration;
				});
				const errors = results.filter((result) => !result);
				if (errors.length === 0) {
					response = common.commonUtils.deepMergeObjects(
						response,
						...results.map((result) => (result && result.configuration) || {}),
					);
				} else {
					this.log('error', {
						configKey: this.configurationKey,
						additionalContext: 'buildConfiguration',
						message: `applicationClientConfigurationIds don't have all available application client configurations in the APPLICATION_CLIENT_CONFIGURATIONS collection`,
						detail: {
							APPLICATION_CLIENT_CONFIGURATIONS: this.devConfig.applicationClientConfigurations,
							APPLICATION_CLIENT_CONFIGURATION_IDS: this.devConfig.applicationClientConfigurationIds,
						},
					});
				}
			} else {
				if (this.authentication && this.authentication.token) {
					const promises: Promise<IApplicationClientConfiguration>[] = [];
					for (const applicationClientConfigurationId of applicationClientConfigurationIds) {
						const existingApplicationClientConfiguration = this.applicationClientConfigurations.find(
							(a) => a.applicationClientConfigurationId === applicationClientConfigurationId,
						);
						if (existingApplicationClientConfiguration) {
							promises.push(
								new Promise((resolve) => {
									resolve(existingApplicationClientConfiguration);
								}),
							);
						}
						const kurtosysApiStore =
							this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>(
								'kurtosysApiStore',
							);
						const queryString: any = {
							applicationClientConfigurationId,
						};
						promises.push(
							kurtosysApiStore.getApplicationClientConfiguration.execute({
								queryString,
							}),
						);
					}
					const results = await Promise.all<IApplicationClientConfiguration>(
						promises.map((p) => p.catch((e) => e)),
					);
					const errors = results.filter((result) => result instanceof Error);
					if (errors.length === 0) {
						const resultsToAdd = results.filter((result) => {
							return !this.applicationClientConfigurations.some(
								(a) => a.applicationClientConfigurationId === result.applicationClientConfigurationId,
							);
						});
						if (resultsToAdd && resultsToAdd.length > 0) {
							this.applicationClientConfigurations = [
								...this.applicationClientConfigurations,
								...resultsToAdd,
							];
						}
						response = common.commonUtils.deepMergeObjects(
							response,
							...results.map((result) => result.configuration),
						);
					} else {
						this.log('error', {
							configKey: this.configurationKey,
							additionalContext: 'buildConfiguration',
							message: 'Failed to fetch Client Config',
							detail: errors,
						});
					}
				}
			}
		}
		if (configuration) {
			response = common.commonUtils.deepMergeObjects(response, configuration);
		}
		return response as TConfiguration;
	}

	@action
	async loadPreviewContext(): Promise<boolean> {
		let response = false;
		const applicationCode = getApplicationCode(this.manifest);
		const previewContext = getPreviewContext(applicationCode);
		if (previewContext) {
			if (previewContext.themeKey && this.themeKey !== previewContext.themeKey) {
				this.themeKey = previewContext.themeKey;
				await this.loadAppTheme();
			}
			if (previewContext.theme && this.rawThemeResponse) {
				this.rawThemeResponse = {
					...this.rawThemeResponse,
					theme: previewContext.theme,
				};
				runInAction(() => {
					this.rawStyles = previewContext.applicationStyles || { ...this.rawStyles };
				});
			}
			if (previewContext.applicationStyles) {
				runInAction(() => {
					this.rawStyles = previewContext.applicationStyles;
				});
			}
			if (previewContext.applicationConfiguration) {
				runInAction(() => {
					this.rawConfiguration = previewContext.applicationConfiguration;
				});
				this.initializeConfiguration(true);
				response = true;
			}
		}
		return response;
	}

	@action
	initializeConfiguration = async (preventAppLoadedEventFire = false) => {
		const kurtosysApiStore =
			this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
		const queryStore = this.storeContext.get<QueryStore<TConfiguration, TComponentStyles>>('queryStore');
		const eventBusStore = this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
		const translationStore =
			this.storeContext.get<TranslationStore<TConfiguration, TComponentStyles>>('translationStore');
		const token =
			(this.authentication && this.authentication.token) ||
			(this.configuration && this.configuration.token) ||
			'';
		kurtosysApiStore.token = token;
		this.loadVersionRegistry();

		if (this.appComponentConfiguration?.groupId) {
			const { GroupedEventManager } = await import('@kurtosys/grouped-event-manager');
			this.groupedEventManager = new GroupedEventManager(
				this.appComponentConfiguration?.groupId,
				this.applicationTypeKey,
			);
		}

		await this.customInitializeBefore();
		eventBusStore.register();
		await this.debounceRequiredPeerEvents(async () => {
			const promises: Promise<any>[] = [
				this.loadAppTheme(),
				translationStore.loadTranslations(),
				queryStore.initialize(),
			];
			await Promise.all(promises.map((p) => p.catch((e) => e)));
			// Load up the inputs from queries defined in the configuration
			this.initializeInputsFromConfiguration();
			await this.debouncePostInitializeConfig(async () => {
				this.setAppCardProps();
				if (this.appCardProps) {
					components.base.UnstyledCard.loadSuperscripts(this.appKey, queryStore, this.appCardProps);
				}
				if (this.customInitializeAfter) {
					await this.customInitializeAfter();
				}
				if (!this.preventAppLoadedEventFire && !preventAppLoadedEventFire) {
					this.setIsBootstrapped();
					this.triggerAppInitialized();
				}
			});
		});
	};

	// If intersection observer is enabled, this will only fire when the app is in view, otherwise it will fire immediately
	async didRender() {
		this.isRendered = true;
		if (this.customDidRender) {
			await this.customDidRender();
		}
	}

	get postInitializeTimeout() {
		return 10000; // TODO: add configuration
	}

	/**
	 * This debounce validates when an application has finished fetching data for its query context, and if it is waiting on child apps.
	 *
	 * @param callback
	 * @param [time=100]
	 * @param [increment=100]
	 */
	async debouncePostInitializeConfig(callback: () => Promise<void>, time = 100, increment = 100) {
		const eventBusStore = this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
		const { useIntersectionObserver } = this.breakpointsFeature;
		const isRendered = useIntersectionObserver ? this.isRendered : true;
		if (time < this.postInitializeTimeout) {
			const workingIncrement = isRendered ? increment : 0;
			setTimeout(async () => {
				this.log('debug', {
					message: 'AppStoreBase.debouncePostInitializeConfig - Validation',
					detail: {
						timePassed: time,
						dataContextStatus: this.dataContextStatus,
						requiredChildCallbacksCompleted: eventBusStore.requiredChildCallbacksCompleted,
					},
				});
				if (this.dataContextStatus === 'READY' && eventBusStore.requiredChildCallbacksCompleted) {
					await callback();
				} else {
					await this.debouncePostInitializeConfig(callback, time + workingIncrement, increment + 50);
				}
			}, workingIncrement);
		} else {
			this.log('error', {
				message: 'Required data context or child events have not loaded in under 10 seconds.',
			});
			await callback();
		}
	}

	get peerEventTimeout() {
		return 5000; // TODO: add configuration
	}

	/**
	 * This debounce validates when an application requires an external or sibling application to fire an event based on listenFor configuration.
	 *
	 * e.g. Pagination requires a table, document explorer or fund center to emit an event in order for it to render.
	 *
	 * @param callback
	 * @param [time=100]
	 */
	async debounceRequiredPeerEvents(callback: () => Promise<void>, time = 100, increment = 100) {
		const eventBusStore = this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
		if (time < this.peerEventTimeout) {
			setTimeout(async () => {
				this.log('debug', {
					message: 'AppStoreBase.debounceRequiredPeerEvents - Validation',
					detail: {
						timePassed: time,
						requiredPeerCallbacksCompleted: eventBusStore.requiredPeerCallbacksCompleted,
					},
				});
				if (eventBusStore.requiredPeerCallbacksCompleted) {
					await callback();
				} else {
					await this.debounceRequiredPeerEvents(callback, time + increment, increment + 50);
				}
			}, increment);
		} else {
			this.log('error', {
				message: 'Required events have not initialized the app in under 5 seconds.',
			});
			await callback();
		}
	}

	triggerAppInitialized = (eventDelay = 0) => {
		const eventBusStore = this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
		const { loadEventDelay = eventDelay } = (this.rawConfiguration && this.rawConfiguration.core) || {};
		setTimeout(() => {
			const eventDetail: eventManagerModels.detail.EventAppInitialisedDetail = {
				type: eventManagerModels.EventTypes.APP_INITIALIZED,
				hasData: this.hasData,
			};
			eventBusStore.trigger(eventDetail);
			this.logDebug(eventManagerModels.EventTypes.APP_INITIALIZED, eventDetail);
			registerAppLoaded(this.manifest, this.appParamsHelper);
		}, loadEventDelay);
	};

	@action setPreventAppLoadedEventFire(shouldPrevent: boolean) {
		this.preventAppLoadedEventFire = shouldPrevent;
	}

	@action loadVersionRegistry = () => {
		const key = '__ksysAppVersionRegistry__';
		if (!(window as any)[key]) {
			(window as any)[key] = [];
		}
		const registry = (window as any)[key];
		const applicationCode = getApplicationCode(this.manifest);
		const templateId = this.manifest.ksysAppTemplateId;
		const version = this.manifest.version;
		registry.push({
			applicationCode,
			templateId,
			version,
		});
	};

	@action
	initializeInputsFromConfiguration = () => {
		const { inputs } = this.coreConfigurations;
		if (inputs) {
			const { key, queries, sourceKeys } = inputs;
			const globalInputs = this.globalInputs;
			if (sourceKeys && sourceKeys.length > 0) {
				sourceKeys.forEach((sourceKey) => {
					const sourceInputs = globalInputs[sourceKey];
					if (sourceInputs) {
						this.appParamsHelper.loadInputsFromConfiguration(sourceInputs);
					}
				});
			}
			if (queries) {
				const values: { [key: string]: any } = {};
				queries.forEach((queryOptions) => {
					const { queryId = 'NO QUERY ID' } = queryOptions;
					const executionOptions = {
						inputs: this.appParamsHelper.inputs,
					};
					const queryStore =
						this.storeContext.get<QueryStore<TConfiguration, TComponentStyles>>('queryStore');
					const value = queryStore.query(queryOptions, executionOptions);
					this.appParamsHelper.loadInputsFromConfiguration({ [queryId]: value });
					values[queryId] = value;
				});
				if (key) {
					this.globalInputs[key] = values;
				}
			}
		}
	};

	inputsWindowKey = '__ksysDynamicInputs__';
	get globalInputs(): { [key: string]: object } {
		if (!(window as any)[this.inputsWindowKey]) {
			(window as any)[this.inputsWindowKey] = {};
		}
		return (window as any)[this.inputsWindowKey];
	}

	set globalInputs(value: { [key: string]: object }) {
		(window as any)[this.inputsWindowKey] = value;
	}

	@computed
	get skeletonLoaders(): ISkeletonLoaders | undefined {
		return this.coreConfigurations && this.coreConfigurations.skeletonLoaders;
	}

	@computed
	get skeletonLoader(): ISkeletonLoader | undefined {
		const skeletonLoaders = this.skeletonLoaders;
		if (skeletonLoaders) {
			const { loaders = [] } = skeletonLoaders;
			return loaders.find((loader) => {
				const {
					id,
					configurationKey: loaderConfigurationKey = 'default',
					styleKey: loaderStyleKey = 'default',
				} = loader;
				const { configurationKey = 'default', styleKey = 'default' } = this.appParamsHelper.rawAppParams || {};
				return (
					id === this.manifest.ksysAppTemplateId &&
					configurationKey === loaderConfigurationKey &&
					styleKey === loaderStyleKey
				);
			});
		}
	}

	@computed
	get rawTheme(): models.ITheme {
		return (this.rawThemeResponse && this.rawThemeResponse.theme) || {};
	}

	@computed
	get show(): boolean {
		if (!this.isBootstrapped) {
			return false;
		}
		if (this.appComponentConfiguration && this.appComponentConfiguration.noDataOptions) {
			const { hide = true } = this.appComponentConfiguration.noDataOptions;
			if (!hide) {
				return true;
			}
		}
		return this.customHasDataValidation(this.hasData);
	}

	@computed
	get showPlaceholder(): boolean {
		return (
			this.show &&
			!this.customHasDataValidation(this.hasData) &&
			(this.noDataPlaceholder !== undefined || this.noDataPlaceholderDisclaimer !== undefined)
		);
	}

	@computed
	get serviceUrl(): string | undefined {
		if (this.storeContext) {
			const kurtosysApiStore =
				this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
			if (kurtosysApiStore) {
				return kurtosysApiStore.serviceUrl;
			}
		}
		return;
	}

	customHasDataValidation(hasData: boolean): boolean {
		if (this.appComponentConfiguration) {
			const { noDataOptions } = this.appComponentConfiguration;
			if (noDataOptions) {
				const { customValidation } = noDataOptions;
				if (customValidation) {
					const queryStore =
						this.storeContext.get<QueryStore<TConfiguration, TComponentStyles>>('queryStore');
					const { includeDefaultValidation, validateWithDefaultAs, conditionalValidation } = customValidation;
					const conditionalHelper = new common.helpers.ConditionalHelper(
						{ queryClass: query.Query },
						conditionalValidation,
					);
					const isValid = conditionalHelper.matchesWithOptions({
						executionOptions: queryStore.executionOptions,
					});
					if (includeDefaultValidation) {
						switch (validateWithDefaultAs) {
							case 'ANY':
								hasData = hasData || isValid;
								break;
							case 'BOTH':
							default:
								hasData = hasData && isValid;
								break;
						}
					} else {
						hasData = isValid;
					}
				}
			}
		}
		return hasData;
	}

	@computed
	get noDataPlaceholder(): string | undefined {
		return (
			this.appComponentConfiguration &&
			this.appComponentConfiguration.noDataOptions &&
			this.appComponentConfiguration.noDataOptions.noDataPlaceholder
		);
	}

	@computed
	get noDataPlaceholderDisclaimer(): components.overview.Disclaimer.models.IDisclaimerProps | undefined {
		const placeHolderText =
			this.appComponentConfiguration &&
			this.appComponentConfiguration.noDataOptions &&
			this.appComponentConfiguration.noDataOptions.noDataPlaceholderDisclaimer;
		return this.mergeQueriesAndProps(placeHolderText);
	}

	@computed
	get dataContextSeed(): IQueryContext | undefined {
		const contextSeedConfiguration = this.coreConfigurations && this.coreConfigurations.contextSeed;
		if (contextSeedConfiguration) {
			const contextAndConfiguration = query.queryUtils.queryHelper.contextStore().get(contextSeedConfiguration);
			if (contextAndConfiguration && contextAndConfiguration.context) {
				return contextAndConfiguration.context;
			}
		}
		const applicationCode = getApplicationCode(this.manifest);
		const keyWithApplicationCode = `${DATA_CONTEXT_SEED_KEY}${applicationCode || ''}`;
		if ((window as any)[keyWithApplicationCode]) {
			return (window as any)[keyWithApplicationCode];
		}
		return undefined;
	}

	@action
	async getAppConfig(): Promise<IGetApplicationAppConfigResponseBody> {
		const appFromHydration = getAppHydration(this.manifest, this.appParamsHelper);
		if (appFromHydration) {
			const { applicationConfiguration, applicationStyles, authentication, applicationClientConfigurationIds } =
				appFromHydration;
			return {
				applicationConfiguration,
				applicationStyles,
				authentication,
				applicationClientConfigurationIds,
			};
		}
		// We want to use the hard coded configuration when developing locally
		// otherwise fetch it
		let response: IGetApplicationAppConfigResponseBody = {
			applicationConfiguration: this.devConfig.configuration && (await this.devConfig.configuration()),
			applicationStyles: this.devConfig.styles && (await this.devConfig.styles()),
			authentication: this.devConfig.authentication && (await this.devConfig.authentication()),
			applicationClientConfigurationIds:
				this.devConfig.applicationClientConfigurationIds &&
				(await this.devConfig.applicationClientConfigurationIds()),
		};

		if (this.mockData) {
			const mockAuth = this.mockData.getWorkingAuthentication() || {};
			const mockEndpoints = this.mockData.getWorkingEndpointConfiguration();
			const mockDataContext = this.mockData.getWorkingDataContext();
			const mockConfig = this.mockData.getWorkingConfiguration();
			response.authentication = { ...response.authentication, ...mockAuth };
			const mockConfiguration: Record<string, any> = {};
			if (mockConfig) {
				mockConfiguration.components = mockConfig;
			}
			if (mockDataContext) {
				if (!mockConfiguration.data) {
					mockConfiguration.data = {};
				}
				mockConfiguration.data.context = mockDataContext;
			}
			if (mockEndpoints) {
				if (!mockConfiguration.core) {
					mockConfiguration.core = {};
				}
				if (!mockConfiguration.core.endpointsConfiguration) {
					mockConfiguration.core.endpointsConfiguration = {};
				}
				mockConfiguration.core.endpointsConfiguration.endpoints = mockEndpoints;
			}
			response.applicationConfiguration = common.commonUtils.deepMergeObjectsWithOptions(
				{ arrayMergeStrategy: 'DeepCopy' },
				response.applicationConfiguration,
				mockConfiguration,
			);
		}

		if (process.env.NODE_ENV === 'production') {
			response = await this.getAppConfigFromApi();
		}
		return response;
	}

	@computed
	get configurationKey() {
		const { rawAppParams = {} } = this.appParamsHelper;
		const { configurationKey = 'default' } = rawAppParams;
		return configurationKey;
	}

	@computed
	get styleKey() {
		const { rawAppParams = {} } = this.appParamsHelper;
		const { styleKey } = rawAppParams;
		return styleKey;
	}

	@computed
	get appKey() {
		const value = getAppKey(this.manifest, this.appParamsHelper);
		return value;
	}

	@action
	async getAppConfigFromApi(): Promise<IGetApplicationAppConfigResponseBody> {
		const urlParams: string[] = [this.configurationKey];
		// Only add the styleKey if it is defined and not default to help with backwards compatibility, until services are deployed.
		if (this.styleKey && this.styleKey !== 'default') {
			urlParams.push(this.styleKey);
		}
		const kurtosysApiStore =
			this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
		return await kurtosysApiStore.getAppConfig.execute({
			urlParams,
		});
	}

	@action
	async loadAppTheme(): Promise<void> {
		const themeKey = this.themeKey;
		if (!this.rawThemeResponse || this.rawThemeResponse.name !== themeKey) {
			const kurtosysApiStore =
				this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
			try {
				const queryString: any = {
					type: 'apps',
					name: themeKey,
				};
				const themeResponse = await kurtosysApiStore.getTheme.execute({
					queryString,
				});
				if (themeResponse) {
					runInAction(() => {
						this.rawThemeResponse = themeResponse;
					});
				}
			} catch (ex) {
				this.log('warning', {
					configKey: this.configurationKey,
					message: `Failed to load theme "${themeKey}"`,
					detail: ex,
				});
			}
		}
	}

	@computed
	get assetsBaseUrl(): string {
		const kurtosysApiStore =
			this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
		return kurtosysApiStore.getBaseAddress('');
	}

	@computed
	get assetCacheOptions(): CacheOptions | undefined {
		return (this.coreConfigurations && this.coreConfigurations.assetCacheOptions) || undefined;
	}

	// Leaving this as uncomputed on purpose
	// Hydration is not using an observable
	get assetRegisters(): IApplicationAssetRegister[] | undefined {
		const appHydration = getAppHydration(this.manifest, this.appParamsHelper);
		if (appHydration && appHydration.app && appHydration.app.assetRegister) {
			return appHydration.app.assetRegister;
		}
	}

	abstract get components();

	@computed
	get appComponentConfiguration(): TConfiguration['components']['app'] {
		const appConfig = this.getComponentConfiguration('app');
		if (appConfig) {
			return appConfig;
		}
		return;
	}

	@computed
	get assets(): models.IAssets | undefined {
		const assets = this.configuration && this.configuration.assetOverrides;
		if (assets && common.commonUtils.isNullOrEmpty(assets.baseUrl)) {
			assets.baseUrl = this.assetsBaseUrl;
		}
		return assets;
	}

	@computed
	get configuration(): TConfiguration | undefined {
		if (this.rawConfiguration) {
			return this.rawConfiguration as TConfiguration;
		}
	}
	@computed
	get styles(): IStyles<TComponentStyles> | undefined {
		if (this.rawStyles) {
			if (this.rawTheme) {
				const { theme: stylesTheme = {}, ...styles } = this.rawStyles;
				const theme = common.commonUtils.deepMergeObjectsWithOptions(
					{ arrayMergeStrategy: 'AppendDeepCopy' },
					this.rawTheme,
					structuredClone(stylesTheme),
				);
				return { theme, ...styles } as IStyles<TComponentStyles>;
			}
			return this.rawStyles as IStyles<TComponentStyles>;
		}
	}
	@computed
	get libraryComponentsConfiguration(): any | undefined {
		return common.commonUtils.deepMergeObjects(
			this.devConfig.libraryComponentsConfiguration,
			(this.configuration && this.configuration.libraryComponents) || {},
			{ appHashId: common.commonUtils.generateHashId(this.storeKey) },
		);
	}
	@computed
	get coreConfigurations(): ICoreConfigurations {
		let response: ICoreConfigurations | undefined;
		if (this.configuration && this.configuration.core) {
			response = this.configuration.core;
		}
		if (!response) {
			response = {};
		}
		return response;
	}
	@computed
	get theme(): any | undefined {
		return this.styles && this.styles.theme;
	}

	@computed
	get responsiveConfiguration():
		| ResponsiveConfig<TConfiguration, TComponentStyles, TConfiguration['components']>
		| undefined {
		if (this.breakpointsFeature.useResponsiveComponentConfiguration && this.configuration) {
			const { components } = this.configuration;
			const breakpoints: models.IBreakpointProps<TConfiguration>[] =
				(this.configuration as any).breakpoints || [];
			return new ResponsiveConfig<TConfiguration, TComponentStyles, TConfiguration['components']>(
				this.storeContext,
				components,
				breakpoints,
				'ApplicationConfig',
			);
		}
		return;
	}

	@computed
	get componentConfiguration(): IComponentConfigurationsBase {
		if (this.breakpointsFeature.useResponsiveComponentConfiguration) {
			if (this.responsiveConfiguration) {
				this.log('debug', {
					additionalContext: Breakpoints.active,
					message: 'Active Responsive Config',
					detail: this.responsiveConfiguration.activeConfiguration,
				});
				return this.responsiveConfiguration.activeConfiguration as IComponentConfigurationsBase;
			}
		} else if (this.configuration && this.configuration.components) {
			return this.configuration.components;
		}
		return {};
	}

	@computed
	get componentStyles(): TComponentStyles {
		if (this.rawStyles && this.styles && this.styles.components) {
			return this.styles.components as TComponentStyles;
		}
		return {} as TComponentStyles;
	}

	@computed
	get isSnapshot(): boolean {
		const value = common.commonUtils.getQueryStringValue({ key: 'isSnapshot' }, window.location.search);
		return value === 'true';
	}

	getKeyFromEventSource(eventSource: eventManagerModels.EventSource) {
		return SourceEventManager.generateKey(eventSource);
	}

	@computed
	get eventSource() {
		const embedInputs = this.appParamsHelper.getAppParamsFromElement();
		return {
			templateId: this.manifest.ksysAppTemplateId,
			version: this.manifest.version,
			applicationId: this.applicationId,
			configurationKey: this.configurationKey,
			styleKey: this.styleKey,
			applicationCode: this.applicationGuid,
			embedInputs: embedInputs ? stringify(embedInputs) : '',
		};
	}

	@computed
	get eventSourceKey() {
		return this.getKeyFromEventSource(this.eventSource);
	}

	groupEventTrigger(detail: eventManagerModels.EventTypeDetail) {
		if (this.groupedEventManager) {
			this.log('debug', {
				message: 'Publishing to group',
				additionalContext: 'AppStoreBase',
				detail: {
					source: this.eventSource,
					eventDetail: detail,
				},
			});
			this.groupedEventManager.publish(this.eventSource, detail);
		}
	}

	groupEventSubscribe<
		TType extends eventManagerModels.EventTypes,
		TDetail extends eventManagerModels.EventTypeDetail,
	>(type: TType, onPublish: (event: eventManagerModels.EventPublish<TDetail>) => void) {
		if (this.groupedEventManager) {
			const subscription = {
				type,
				onPublish,
				listener: this.eventSource,
			};
			this.log('debug', {
				message: 'Subscribing to group',
				additionalContext: 'AppStoreBase',
				detail: subscription,
			});
			this.groupedEventManager.subscribe(subscription);
		}
	}

	getInput(inputKey: keyof IInputs) {
		return this.appParamsHelper.inputs && this.appParamsHelper.inputs[inputKey];
	}
	getComponentConfiguration<K extends keyof IComponentConfigurationsBase>(componentKey: K) {
		return this.componentConfiguration[componentKey];
	}
	extractComponentStyle(style: IComponentStyle, ...childKeys: string[]) {
		let response = style;
		if (childKeys) {
			for (const childKey of childKeys) {
				if (response && (response as any).children && (response as any).children[childKey]) {
					response = (response as any).children[childKey];
				} else {
					response = undefined;
					break;
				}
			}
		}
		return response;
	}
	getComponentStyle<K extends keyof TComponentStyles>(
		componentKey: K,
		...childKeys: string[]
	): IComponentStyle | undefined {
		return this.extractComponentStyle(this.componentStyles[componentKey] as IComponentStyle, ...childKeys);
	}
	getDimensions = (): IPixelDimensions => {
		return common.commonUtils.getHtmlElementSize(this.htmlElement);
	};
	getCoreEmbed(embedId: string): components.base.ApplicationEmbed.models.IApplicationEmbedProps | undefined {
		if (
			this.configuration &&
			this.configuration.core &&
			this.configuration.core.embedding &&
			this.configuration.core.embedding.embeds
		) {
			const embed = this.configuration.core.embedding.embeds.find((embed) => embed.embedId === embedId);
			const hostname = this.configuration.core.embedding.hostname;
			if (embed) {
				const response: components.base.ApplicationEmbed.models.IApplicationEmbedProps = { ...embed };
				if (hostname && !response.hostname) {
					response.hostname = hostname;
				}
				return response;
			}
		}
	}
	orchestrateEmbed(
		rawEmbed: Partial<components.base.ApplicationEmbed.models.IApplicationEmbedProps>,
		defaultTemplateId: string,
		inputs?: components.base.ApplicationEmbed.models.IApplicationEmbedInput[],
		onLoad?: (element: HTMLElement) => void,
	): components.base.ApplicationEmbed.models.IApplicationEmbedProps {
		const embedId = rawEmbed && rawEmbed.embedId;
		const coreEmbed: Partial<components.base.ApplicationEmbed.models.IApplicationEmbedProps> =
			(embedId && this.getCoreEmbed(embedId)) || {};
		const props: components.base.ApplicationEmbed.models.IApplicationEmbedProps =
			common.commonUtils.deepMergeObjects(
				{
					templateId: defaultTemplateId,
					hostname: this.manifest.getBaseUrl(''),
				},
				coreEmbed,
				rawEmbed,
			);
		props.inputs = [...(props.inputs || []), ...(inputs || [])];
		if (onLoad) {
			props.onLoad = onLoad;
		}
		return props;
	}

	@computed
	get globalFormatsByKey(): IGlobalFormatsByKey {
		const globalQueryFormats = (this.coreConfigurations && this.coreConfigurations.globalQueryFormats) || [];
		return common.commonUtils.pivotCollection(globalQueryFormats, (item) => item.key);
	}

	@observable.ref
	appCardProps: components.base.Card.models.ICardProps | undefined;

	@action
	setAppCardProps(): void {
		// in some projects we don't have cardProps within the app configuration (hence the 'anyæ to prevent type breaks)
		const cardPropsRaw = this.appComponentConfiguration && (this.appComponentConfiguration as any).cardProps;
		if (cardPropsRaw) {
			const cardProps: components.base.Card.models.ICardProps | undefined =
				this.mergeQueriesAndProps(cardPropsRaw) || {};
			cardProps.titleId = cardProps.titleId || `${getAppKey(this.manifest, this.appParamsHelper)}`;

			if (cardProps && !cardProps.footerDisclaimers) {
				const globalDisclaimerStore = getGlobalDisclaimerStore();
				if (globalDisclaimerStore) {
					const disclaimerOptions = this.coreConfigurations.disclaimerOptions;
					const level = (disclaimerOptions && disclaimerOptions.level) || GlobalDisclaimerLevel.app;
					const disclaimers = globalDisclaimerStore.get(level, this.appKey);
					if (disclaimers && disclaimers.length > 0) {
						const separator = (disclaimerOptions && disclaimerOptions.separator) || ' ';
						cardProps.footerDisclaimers = {
							separator,
							disclaimers: disclaimers.map((disclaimer) => {
								return {
									text: disclaimer.text,
									hasHtmlContent: disclaimer.hasHtml,
									superscript: disclaimer.superscript.toString(),
								};
							}),
						};
					}
				}
			}
			this.logDebug('App Store Base > Set App Card Props', cardProps);
			this.appCardProps = cardProps;
		}
	}

	@action
	public getUser = async () => {
		if (!this.user) {
			const kurtosysApiStore =
				this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
			this.user = await kurtosysApiStore.getUserByToken.execute();
			await this.onGetUser();
		}
		return this.user;
	};

	@action
	public onGetUser = async () => {
		// Setup to override
	};
}
